import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';
import { IUser } from '../../UserSlice';
import { RootState } from '../..';
import {
  adminCreateOneToOneTutorRateVersion,
  adminGetOneToOneTutorRateVersions,
  adminGetOneToOneTutorRateVersionsForDropdown,
  adminUpdateOneToOneTutorRateVersion,
  adminDeleteOneToOneTutorRateVersion,
  adminVerifyOneToOneTutorRateVersionDelete,
} from './adminApi';

export const ONE_TO_ONE_TUTOR_RATE_VERSION_CONDITION_TYPES = {
  STUDENT_ASSIGNED_DATE: 'STUDENT_ASSIGNED_DATE',
  STUDENT_REGISTRATION_DATE: 'STUDENT_REGISTRATION_DATE',
} as const;

export type TOneToOneTutorRateVersionConditionTypes = typeof ONE_TO_ONE_TUTOR_RATE_VERSION_CONDITION_TYPES[keyof typeof ONE_TO_ONE_TUTOR_RATE_VERSION_CONDITION_TYPES];

export const ONE_TO_ONE_TUTOR_RATE_VERSION_STUDENT_ASSIGNED_DATE_CONDITIONS = {
  GREATER_THAN: 'GREATER_THAN',
  LESS_THAN: 'LESS_THAN',
  EQUAL: 'EQUAL',
  GREATER_THAN_OR_EQUAL: 'GREATER_THAN_OR_EQUAL',
  LESS_THAN_OR_EQUAL: 'LESS_THAN_OR_EQUAL',
} as const;

export const ONE_TO_ONE_TUTOR_RATE_VERSION_STUDENT_REGISTRATION_DATE_CONDITIONS = {
  GREATER_THAN: 'GREATER_THAN',
  LESS_THAN: 'LESS_THAN',
  EQUAL: 'EQUAL',
  GREATER_THAN_OR_EQUAL: 'GREATER_THAN_OR_EQUAL',
  LESS_THAN_OR_EQUAL: 'LESS_THAN_OR_EQUAL',
} as const;

export type TOneToOneTutorRateVersionStudentAssignedDateConditionTypes = typeof ONE_TO_ONE_TUTOR_RATE_VERSION_STUDENT_ASSIGNED_DATE_CONDITIONS[keyof typeof ONE_TO_ONE_TUTOR_RATE_VERSION_STUDENT_ASSIGNED_DATE_CONDITIONS];

export type TOneToOneTutorRateVersionStudentRegistrationDateConditionTypes = typeof ONE_TO_ONE_TUTOR_RATE_VERSION_STUDENT_REGISTRATION_DATE_CONDITIONS[keyof typeof ONE_TO_ONE_TUTOR_RATE_VERSION_STUDENT_REGISTRATION_DATE_CONDITIONS];

export interface IOneToOneTutorRateVersionStudentAssignedDateCondition {
  _id: string;
  type: 'STUDENT_ASSIGNED_DATE';
  logic: TOneToOneTutorRateVersionStudentAssignedDateConditionTypes;
  date: string;
}

export interface IOneToOneTutorRateVersionStudentRegistrationDateCondition {
  _id: string;
  type: 'STUDENT_REGISTRATION_DATE';
  logic: TOneToOneTutorRateVersionStudentRegistrationDateConditionTypes;
  date: string;
}

export type TOneToOneTutorRateVersionCondition =
  | IOneToOneTutorRateVersionStudentAssignedDateCondition
  | IOneToOneTutorRateVersionStudentRegistrationDateCondition;

export interface IOneToOneTutorRateVersion {
  _id: string;
  name: string;
  conditions: TOneToOneTutorRateVersionCondition[];
  hideToUser: boolean;
  createdBy: string | IUser;
  updatedBy: string | IUser;
}

export interface IOneToOneTutorRateVersionDeleteVerificationData {
  oneToOneTutorRateCount: number;
}

export interface IInitialOneToOneTutorRateVersionsState {
  status: 'idle' | 'loading' | 'rejected';
  // ADMIN
  adminOneToOneTutorRateVersionListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IOneToOneTutorRateVersion>;
  };
  adminOneToOneTutorRateVersionListDataForDropDown?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IOneToOneTutorRateVersion>;
  };
  adminOneToOneTutorRateVersionDeleteVerificationData?: IOneToOneTutorRateVersionDeleteVerificationData;
}

const initialState: IInitialOneToOneTutorRateVersionsState = {
  status: 'idle',
  // ADMIN
  adminOneToOneTutorRateVersionListData: undefined,
  adminOneToOneTutorRateVersionListDataForDropDown: undefined,
  adminOneToOneTutorRateVersionDeleteVerificationData: undefined,
};

export const OneToOneTutorRateVersionsSlice = createSlice({
  initialState,
  name: 'oneToOneTutorRateVersions',
  reducers: {
    // ADMIN
    resetAdminOneToOneTutorRateVersionListData: (state) => {
      state.adminOneToOneTutorRateVersionListData = undefined;
    },
    resetAdminOneToOneTutorRateVersionListDataForDropDown: (state) => {
      state.adminOneToOneTutorRateVersionListDataForDropDown = undefined;
    },
    resetAdminOneToOneTutorRateVersionDeleteVerificationData: (state) => {
      state.adminOneToOneTutorRateVersionDeleteVerificationData = undefined;
    },
  },
  extraReducers(builder) {
    builder
      // ADMIN
      .addCase(adminGetOneToOneTutorRateVersions.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminOneToOneTutorRateVersionListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminOneToOneTutorRateVersionListData = newAdminOneToOneTutorRateVersionListData;
      })
      .addCase(
        adminGetOneToOneTutorRateVersionsForDropdown.fulfilled,
        (state, action) => {
          const result = action.payload;

          const newAdminOneToOneTutorRateVersionListDataForDropDown = {
            limit: result.limit,
            offset: result.offset,
            totalDocs: result.totalDocs || 0,
            list: result.docs || [],
          };

          state.adminOneToOneTutorRateVersionListDataForDropDown = newAdminOneToOneTutorRateVersionListDataForDropDown;
        }
      )
      .addCase(
        adminCreateOneToOneTutorRateVersion.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (state.adminOneToOneTutorRateVersionListData) {
            const newAdminOneToOneTutorRateVersionListData = {
              ...state.adminOneToOneTutorRateVersionListData,
            };

            newAdminOneToOneTutorRateVersionListData.list.unshift(result);

            if (newAdminOneToOneTutorRateVersionListData.totalDocs) {
              newAdminOneToOneTutorRateVersionListData.totalDocs += 1;
            } else {
              newAdminOneToOneTutorRateVersionListData.totalDocs = 1;
            }

            state.adminOneToOneTutorRateVersionListData = newAdminOneToOneTutorRateVersionListData;
          }
        }
      )
      .addCase(
        adminUpdateOneToOneTutorRateVersion.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (
            state.adminOneToOneTutorRateVersionListData &&
            state.adminOneToOneTutorRateVersionListData.list &&
            state.adminOneToOneTutorRateVersionListData.list.length
          ) {
            const newAdminOneToOneTutorRateVersionListData = {
              ...state.adminOneToOneTutorRateVersionListData,
            };

            const index = newAdminOneToOneTutorRateVersionListData.list.findIndex(
              (item) => item._id === result._id
            );

            if (index > -1) {
              newAdminOneToOneTutorRateVersionListData.list[index] = result;
            }

            state.adminOneToOneTutorRateVersionListData = newAdminOneToOneTutorRateVersionListData;
          }
        }
      )
      .addCase(
        adminDeleteOneToOneTutorRateVersion.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (
            state.adminOneToOneTutorRateVersionListData &&
            state.adminOneToOneTutorRateVersionListData.list &&
            state.adminOneToOneTutorRateVersionListData.list.length
          ) {
            const newAdminOneToOneTutorRateVersionListData = {
              ...state.adminOneToOneTutorRateVersionListData,
            };

            newAdminOneToOneTutorRateVersionListData.totalDocs = newAdminOneToOneTutorRateVersionListData.totalDocs
              ? newAdminOneToOneTutorRateVersionListData.totalDocs - 1
              : 0;

            newAdminOneToOneTutorRateVersionListData.list = newAdminOneToOneTutorRateVersionListData.list.filter(
              (item) => item._id !== result._id
            );

            state.adminOneToOneTutorRateVersionListData = newAdminOneToOneTutorRateVersionListData;
          }
        }
      )
      .addCase(
        adminVerifyOneToOneTutorRateVersionDelete.fulfilled,
        (state, action) => {
          const result = action.payload;

          state.adminOneToOneTutorRateVersionDeleteVerificationData = result;
        }
      );

    // Common Matchers
    builder.addMatcher(
      isFulfilled(
        // ADMIN
        adminCreateOneToOneTutorRateVersion,
        adminGetOneToOneTutorRateVersions,
        adminUpdateOneToOneTutorRateVersion,
        adminDeleteOneToOneTutorRateVersion,
        adminGetOneToOneTutorRateVersionsForDropdown,
        adminVerifyOneToOneTutorRateVersionDelete
      ),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isPending(
        // ADMIN
        adminCreateOneToOneTutorRateVersion,
        adminGetOneToOneTutorRateVersions,
        adminUpdateOneToOneTutorRateVersion,
        adminDeleteOneToOneTutorRateVersion,
        adminGetOneToOneTutorRateVersionsForDropdown,
        adminVerifyOneToOneTutorRateVersionDelete
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isRejected(
        // ADMIN
        adminCreateOneToOneTutorRateVersion,
        adminGetOneToOneTutorRateVersions,
        adminUpdateOneToOneTutorRateVersion,
        adminDeleteOneToOneTutorRateVersion,
        adminGetOneToOneTutorRateVersionsForDropdown,
        adminVerifyOneToOneTutorRateVersionDelete
      ),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const getOneToOneTutorRateVersionsState = (
  state: RootState
): RootState['oneToOneTutorRateVersions'] => state.oneToOneTutorRateVersions;

export const {
  resetAdminOneToOneTutorRateVersionListData,
  resetAdminOneToOneTutorRateVersionListDataForDropDown,
  resetAdminOneToOneTutorRateVersionDeleteVerificationData,
} = OneToOneTutorRateVersionsSlice.actions;

export * from './adminApi';
export default OneToOneTutorRateVersionsSlice.reducer;
